import React from 'react';
import { AlertCircle, Info, TrendingUp, Target } from 'lucide-react';
import { formatNumber, formatPercentage } from '../utils/saps3Utils';
import {
  UnimedCard,
  UnimedCardHeader,
  UnimedCardTitle,
  UnimedCardContent,
  UnimedGrid,
  UnimedBadge,
  UnimedAlert,
  UnimedAlertDescription
} from './ui/UnimedComponents';

const LatinAmericaComparison = ({ stats }) => {
  if (!stats || !stats.latinAmericaReference) {
    return null;
  }

  const smrDifference = stats.smr - stats.smrFromPercentage;
  const expectedMortalityDifference = stats.expectedMortality - stats.expectedMortalityFromPercentage;

  return (
    <UnimedCard>
      <UnimedCardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="bg-gradient-to-br from-unimed-lightblue-500 to-unimed-lightblue-600 p-3 rounded-xl mr-4">
              <Target className="h-6 w-6 text-white" />
            </div>
            <div>
              <UnimedCardTitle size="xl">Comparação de Referências SAPS3</UnimedCardTitle>
              <p className="unimed-texto-corrido text-sm text-unimed-darkgreen-600">América Latina vs. Percentuais Originais</p>
            </div>
          </div>
          <UnimedBadge variant="info">
            REFERÊNCIA AMÉRICA LATINA ATIVA
          </UnimedBadge>
        </div>
      </UnimedCardHeader>

      <UnimedCardContent>
        <UnimedGrid cols={2}>
          {/* SMR Comparison */}
          <div className="bg-gradient-to-br from-unimed-lightblue-50 to-unimed-lightblue-100 rounded-xl p-6 border border-unimed-lightblue-200">
            <div className="flex items-center mb-4">
              <AlertCircle className="h-5 w-5 text-unimed-lightblue-600 mr-2" />
              <h4 className="unimed-titulo-secundario font-semibold text-unimed-lightblue-800">SMR (RAZÃO DE MORTALIDADE PADRONIZADA)</h4>
            </div>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="unimed-texto-corrido text-sm text-unimed-lightblue-700">América Latina (Atual):</span>
                <span className="unimed-titulo-secundario text-lg font-bold text-unimed-lightblue-900">{formatNumber(stats.smr, 3)}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="unimed-texto-corrido text-sm text-unimed-darkgreen-600">Percentuais Originais:</span>
                <span className="unimed-titulo-secundario text-lg font-medium text-unimed-darkgreen-700">{formatNumber(stats.smrFromPercentage, 3)}</span>
              </div>
              
              <div className="border-t border-unimed-lightblue-200 pt-3">
                <div className="flex justify-between items-center">
                  <span className="unimed-chapeu text-sm font-medium text-unimed-lightblue-700">DIFERENÇA:</span>
                  <span className={`unimed-titulo-secundario text-lg font-bold ${
                    smrDifference > 0 ? 'text-red-600' : 'text-unimed-green-600'
                  }`}>
                    {smrDifference > 0 ? '+' : ''}{formatNumber(smrDifference, 3)}
                  </span>
                </div>
                <p className="unimed-texto-corrido text-xs text-unimed-darkgreen-500 mt-1">
                  {smrDifference > 0 ? 'Maior risco com ref. América Latina' : 'Menor risco com ref. América Latina'}
                </p>
              </div>
            </div>
          </div>

          {/* Expected Mortality Comparison */}
          <div className="bg-gradient-to-br from-unimed-green-50 to-unimed-green-100 rounded-xl p-6 border border-unimed-green-200">
            <div className="flex items-center mb-4">
              <TrendingUp className="h-5 w-5 text-unimed-green-600 mr-2" />
              <h4 className="unimed-titulo-secundario font-semibold text-unimed-green-800">MORTALIDADE ESPERADA</h4>
            </div>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="unimed-texto-corrido text-sm text-unimed-green-700">América Latina (Atual):</span>
                <span className="unimed-titulo-secundario text-lg font-bold text-unimed-green-900">{formatPercentage(stats.expectedMortality, 1)}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="unimed-texto-corrido text-sm text-unimed-darkgreen-600">Percentuais Originais:</span>
                <span className="unimed-titulo-secundario text-lg font-medium text-unimed-darkgreen-700">{formatPercentage(stats.expectedMortalityFromPercentage, 1)}</span>
              </div>
              
              <div className="border-t border-unimed-green-200 pt-3">
                <div className="flex justify-between items-center">
                  <span className="unimed-chapeu text-sm font-medium text-unimed-green-700">DIFERENÇA:</span>
                  <span className={`unimed-titulo-secundario text-lg font-bold ${
                    expectedMortalityDifference > 0 ? 'text-red-600' : 'text-unimed-green-600'
                  }`}>
                    {expectedMortalityDifference > 0 ? '+' : ''}{formatNumber(expectedMortalityDifference, 1)}%
                  </span>
                </div>
                <p className="unimed-texto-corrido text-xs text-unimed-darkgreen-500 mt-1">
                  Mortalidade {expectedMortalityDifference > 0 ? 'mais alta' : 'mais baixa'} com tabela América Latina
                </p>
              </div>
            </div>
          </div>
        </UnimedGrid>

        {/* Summary and Interpretation */}
        <UnimedAlert variant="info">
          <UnimedAlertDescription>
            <div className="flex items-start">
              <Info className="h-5 w-5 text-unimed-lightblue-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h5 className="unimed-titulo-secundario font-medium text-unimed-darkgreen-800 mb-2">INTERPRETAÇÃO CLÍNICA</h5>
                <div className="unimed-texto-corrido text-sm text-unimed-darkgreen-700 space-y-2">
                  <p>
                    • A <strong>tabela de referência da América Latina</strong> foi desenvolvida especificamente para populações latino-americanas, 
                    oferecendo predições mais precisas para sua população de UTI.
                  </p>
                  <p>
                    • SMR baseado na referência latino-americana: <strong>{formatNumber(stats.smr, 3)}</strong>
                    {stats.smr > 1.1 ? ' indica mortalidade acima do esperado' : 
                     stats.smr < 0.9 ? ' indica excelente performance' : 
                     ' indica performance dentro do esperado'}.
                  </p>
                  <p>
                    • Esta referência é mais apropriada para benchmarking e comparação com outras UTIs da região.
                  </p>
                </div>
              </div>
            </div>
          </UnimedAlertDescription>
        </UnimedAlert>

        {/* Quality Metrics */}
        <UnimedGrid cols={4} className="mt-6">
          <div className="bg-white border border-unimed-beige-300 rounded-lg p-4 text-center">
            <div className="unimed-titulo-principal text-lg font-bold text-unimed-lightblue-600">
              {stats.totalPatients.toLocaleString()}
            </div>
            <div className="unimed-chapeu text-xs text-unimed-darkgreen-500 uppercase tracking-wider">
              TOTAL PACIENTES
            </div>
          </div>
          
          <div className="bg-white border border-unimed-beige-300 rounded-lg p-4 text-center">
            <div className="unimed-titulo-principal text-lg font-bold text-red-600">
              {stats.observedDeaths}
            </div>
            <div className="unimed-chapeu text-xs text-unimed-darkgreen-500 uppercase tracking-wider">
              ÓBITOS OBSERVADOS
            </div>
          </div>
          
          <div className="bg-white border border-unimed-beige-300 rounded-lg p-4 text-center">
            <div className="unimed-titulo-principal text-lg font-bold text-unimed-green-600">
              {formatNumber(stats.expectedDeaths, 1)}
            </div>
            <div className="unimed-chapeu text-xs text-unimed-darkgreen-500 uppercase tracking-wider">
              ÓBITOS ESPERADOS (LA)
            </div>
          </div>
          
          <div className="bg-white border border-unimed-beige-300 rounded-lg p-4 text-center">
            <div className={`unimed-titulo-principal text-lg font-bold ${
              stats.smr > 1.1 ? 'text-red-600' : 
              stats.smr < 0.9 ? 'text-unimed-green-600' : 
              'text-unimed-orange-600'
            }`}>
              {formatNumber(stats.smr, 2)}
            </div>
            <div className="unimed-chapeu text-xs text-unimed-darkgreen-500 uppercase tracking-wider">
              SMR (LA)
            </div>
          </div>
        </UnimedGrid>
      </UnimedCardContent>
    </UnimedCard>
  );
};

export default LatinAmericaComparison;
